import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import { formatCurrency } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// ── Shared primitives ──────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = 'var(--primary)', icon }) {
  return (
    <div className="card" style={{ padding: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
          {sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: 4 }}>{sub}</div>}
        </div>
        {icon && <span style={{ fontSize: '1.4rem', opacity: 0.5 }}>{icon}</span>}
      </div>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{title}</h2>
      {subtitle && <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', margin: '0.2rem 0 0' }}>{subtitle}</p>}
    </div>
  );
}

// ── Bar chart (horizontal) ─────────────────────────────────────────────────

function HBar({ label, value, max, color = 'var(--primary)', format }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const display = format ? format(value) : value.toLocaleString();
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-2)', textTransform: 'capitalize' }}>{label}</span>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-1)' }}>{display}</span>
      </div>
      <div style={{ height: 6, background: 'var(--surface-alt)', borderRadius: 99 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
}

// ── Sparkline bar chart (vertical, for trends) ─────────────────────────────

function SparkBars({ data, valueKey, labelKey, color = 'var(--primary)', format }) {
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
      {data.map((d, i) => {
        const pct = ((d[valueKey] || 0) / max) * 100;
        const isLast = i === data.length - 1;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }} title={`${d[labelKey]}: ${format ? format(d[valueKey]) : (d[valueKey] || 0).toLocaleString()}`}>
            <div style={{ width: '100%', background: isLast ? color : `${color}55`, borderRadius: '3px 3px 0 0', height: `${pct}%`, minHeight: 2, transition: 'height 0.4s ease' }} />
          </div>
        );
      })}
    </div>
  );
}

function SparkLabels({ data, labelKey }) {
  const step = Math.ceil(data.length / 6);
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, fontSize: '0.55rem', color: 'var(--text-3)', textAlign: 'center', overflow: 'hidden' }}>
          {i % step === 0 ? d[labelKey]?.slice(5) : ''}
        </div>
      ))}
    </div>
  );
}

// ── Pie-like donut ring (CSS only) ────────────────────────────────────────

function DonutLegend({ items }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {items.map(({ label, value, color, pct }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
          <span style={{ fontSize: '0.8rem', color: 'var(--text-2)', flex: 1, textTransform: 'capitalize' }}>{label}</span>
          <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{value.toLocaleString()}</span>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-3)', width: 36, textAlign: 'right' }}>{pct}%</span>
        </div>
      ))}
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────

const TABS = ['Users', 'Loans & Sales', 'NPL & Risk'];
const TIER_COLORS = { basic: '#94a3b8', standard: '#5B8EFF', gold: '#F5A623', premium: '#818CF8' };
const KYC_COLORS = { approved: '#00C896', pending: '#5B8EFF', under_review: '#F5A623', rejected: '#FF4757', resubmit: '#ff9900', not_submitted: '#4A556A' };
const TYPE_COLORS = ['#00C896', '#5B8EFF', '#818CF8', '#F5A623', '#FF4757', '#ff9900'];

// ── User Analytics panel ───────────────────────────────────────────────────

function UserAnalytics() {
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-users'],
    queryFn: () => adminService.getUserAnalytics().then(r => r.data.data),
  });

  if (isLoading) return <LoadingSpinner />;
  if (!data) return null;

  const { summary, tier_distribution, kyc_distribution, monthly_registrations } = data;

  const tierItems = Object.entries(tier_distribution).map(([k, v]) => ({
    label: k, value: v, color: TIER_COLORS[k],
    pct: summary.total_users > 0 ? Math.round((v / summary.total_users) * 100) : 0,
  }));

  const kycTotal = Object.values(kyc_distribution).reduce((a, b) => a + b, 0);
  const kycItems = Object.entries(kyc_distribution).map(([k, v]) => ({
    label: k.replace(/_/g, ' '), value: v, color: KYC_COLORS[k],
    pct: kycTotal > 0 ? Math.round((v / kycTotal) * 100) : 0,
  }));

  const growthColor = summary.growth_pct >= 0 ? '#00C896' : '#FF4757';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
        <StatCard label="Total Customers" value={summary.total_users.toLocaleString()} icon="○" />
        <StatCard label="Active" value={summary.active_users.toLocaleString()} color="#00C896" icon="✓" />
        <StatCard label="New This Month" value={summary.new_this_month.toLocaleString()}
          sub={<span style={{ color: growthColor }}>{summary.growth_pct >= 0 ? '+' : ''}{summary.growth_pct}% vs last month</span>}
          color="var(--primary)" icon="↑" />
        <StatCard label="Email Verified" value={`${summary.total_users > 0 ? Math.round((summary.email_verified / summary.total_users) * 100) : 0}%`}
          sub={`${summary.email_verified.toLocaleString()} of ${summary.total_users.toLocaleString()}`} icon="✉" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {/* Tier distribution */}
        <div className="card">
          <SectionHeader title="Tier Distribution" />
          <DonutLegend items={tierItems} />
          <div style={{ marginTop: '1rem' }}>
            {tierItems.map(({ label, value, color }) => (
              <HBar key={label} label={label} value={value} max={summary.total_users} color={color} />
            ))}
          </div>
        </div>

        {/* KYC status */}
        <div className="card">
          <SectionHeader title="KYC Status Distribution" />
          <DonutLegend items={kycItems} />
          <div style={{ marginTop: '1rem' }}>
            {kycItems.map(({ label, value, color }) => (
              <HBar key={label} label={label} value={value} max={kycTotal} color={color} />
            ))}
          </div>
        </div>
      </div>

      {/* Monthly registrations sparkline */}
      <div className="card">
        <SectionHeader title="Monthly Registrations (12 months)" subtitle="New customer sign-ups per month" />
        <SparkBars data={monthly_registrations} valueKey="count" labelKey="month" color="var(--primary)" />
        <SparkLabels data={monthly_registrations} labelKey="month" />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>Peak: {Math.max(...monthly_registrations.map(d => d.count)).toLocaleString()} users</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 600 }}>
            Latest: {monthly_registrations[monthly_registrations.length - 1]?.count || 0} users
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Loan Analytics panel ───────────────────────────────────────────────────

function LoanAnalytics() {
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-loans'],
    queryFn: () => adminService.getLoanAnalytics().then(r => r.data.data),
  });

  if (isLoading) return <LoadingSpinner />;
  if (!data) return null;

  const { summary, status_funnel, loan_type_breakdown, monthly_trends } = data;
  const maxFunnel = Math.max(...status_funnel.map(s => s.count), 1);
  const maxType = Math.max(...(loan_type_breakdown || []).map(t => t.total), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
        <StatCard label="Total Disbursed" value={formatCurrency(summary.total_disbursed)} color="#00C896" icon="₦" />
        <StatCard label="Outstanding Portfolio" value={formatCurrency(summary.total_outstanding)} color="#5B8EFF" icon="⬡" />
        <StatCard label="Approval Rate" value={`${summary.approval_rate}%`} color="#00C896" icon="✓"
          sub={`${summary.rejection_rate}% rejected`} />
        <StatCard label="This Month Disbursed" value={formatCurrency(summary.disbursed_this_month)}
          sub={`vs ${formatCurrency(summary.disbursed_last_month)} last month`} icon="↑" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
        <StatCard label="Avg Loan Amount" value={formatCurrency(summary.avg_loan_amount)} icon="◈" />
        <StatCard label="Avg Tenure" value={`${summary.avg_tenure_months} months`} icon="◷" />
        <StatCard label="Offer Acceptance" value={`${summary.offer_acceptance_rate}%`} color="#F5A623" icon="💌" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {/* Application funnel */}
        <div className="card">
          <SectionHeader title="Application Pipeline" subtitle="Loans by current status" />
          {status_funnel.filter(s => s.count > 0).map(({ status, count }) => (
            <HBar key={status} label={status.replace(/_/g, ' ')} value={count} max={maxFunnel}
              color={['approved','active','closed'].includes(status) ? '#00C896' : status === 'rejected' || status === 'defaulted' ? '#FF4757' : '#5B8EFF'} />
          ))}
        </div>

        {/* Loan type breakdown */}
        <div className="card">
          <SectionHeader title="Loan Type Mix" subtitle="By total disbursed value" />
          {(loan_type_breakdown || []).map(({ type, count, total, avg }, i) => (
            <HBar key={type} label={`${type} (${count})`} value={total} max={maxType}
              color={TYPE_COLORS[i % TYPE_COLORS.length]} format={formatCurrency} />
          ))}
          {!loan_type_breakdown?.length && <p style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>No disbursed loans yet.</p>}
        </div>
      </div>

      {/* Monthly trends — two sparklines */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div className="card">
          <SectionHeader title="Monthly Disbursements" subtitle="Last 12 months" />
          <SparkBars data={monthly_trends} valueKey="disbursed" labelKey="month" color="#00C896" format={formatCurrency} />
          <SparkLabels data={monthly_trends} labelKey="month" />
        </div>
        <div className="card">
          <SectionHeader title="Monthly Collections" subtitle="Payments received per month" />
          <SparkBars data={monthly_trends} valueKey="collected" labelKey="month" color="#5B8EFF" format={formatCurrency} />
          <SparkLabels data={monthly_trends} labelKey="month" />
        </div>
      </div>

      {/* Key metrics row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
        <StatCard label="Total Interest Income" value={formatCurrency(summary.total_interest_income)} color="#818CF8" icon="%" />
        <StatCard label="Collections This Month" value={formatCurrency(summary.collections_this_month)} color="#00C896" icon="💰" />
      </div>
    </div>
  );
}

// ── NPL Analytics panel ────────────────────────────────────────────────────

function NplAnalytics() {
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-npl'],
    queryFn: () => adminService.getNplAnalytics().then(r => r.data.data),
  });

  if (isLoading) return <LoadingSpinner />;
  if (!data) return null;

  const { summary, aging_analysis, npl_trend } = data;

  const nplRatioColor = summary.npl_ratio < 5 ? '#00C896' : summary.npl_ratio < 10 ? '#F5A623' : '#FF4757';

  const agingItems = [
    { label: '1–30 days', ...aging_analysis['1_30'], color: '#F5A623' },
    { label: '31–60 days', ...aging_analysis['31_60'], color: '#ff9900' },
    { label: '61–90 days', ...aging_analysis['61_90'], color: '#FF6B35' },
    { label: '91+ days', ...aging_analysis['91_plus'], color: '#FF4757' },
  ];
  const maxAging = Math.max(...agingItems.map(a => a.amount), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* NPL ratio hero */}
      <div style={{
        padding: '1.5rem', borderRadius: 16, border: '1px solid var(--border)',
        background: `linear-gradient(135deg, ${nplRatioColor}15, transparent)`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>NPL Ratio</div>
            <div style={{ fontSize: '3rem', fontWeight: 800, color: nplRatioColor, lineHeight: 1 }}>{summary.npl_ratio}%</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: 4 }}>
              {summary.npl_ratio < 5 ? '✓ Within acceptable range (<5%)' : summary.npl_ratio < 10 ? '⚠ Elevated — monitor closely (5–10%)' : '✗ High — immediate attention required (>10%)'}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ height: 12, background: 'var(--surface-alt)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(summary.npl_ratio * 5, 100)}%`, height: '100%', background: nplRatioColor, borderRadius: 99 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontSize: '0.65rem', color: '#00C896' }}>0% — Healthy</span>
              <span style={{ fontSize: '0.65rem', color: '#F5A623' }}>5%</span>
              <span style={{ fontSize: '0.65rem', color: '#FF4757' }}>10%+</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
        <StatCard label="NPL Loans" value={summary.npl_count.toLocaleString()} color="#FF4757" icon="⚠" />
        <StatCard label="NPL Value" value={formatCurrency(summary.npl_value)} color="#FF4757" />
        <StatCard label="At-Risk Active" value={summary.at_risk_count.toLocaleString()} color="#F5A623" icon="◉"
          sub={`${formatCurrency(summary.at_risk_value)}`} />
        <StatCard label="Recovery Rate" value={`${summary.recovery_rate}%`}
          color={summary.recovery_rate >= 60 ? '#00C896' : '#F5A623'}
          sub={`${formatCurrency(summary.recovery_amount)} collected`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {/* Aging analysis */}
        <div className="card">
          <SectionHeader title="Overdue Aging Analysis" subtitle="Outstanding balance by days past due" />
          {agingItems.map(({ label, count, amount, color }) => (
            <div key={label} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{label}</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color }}>{count} loans</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginLeft: 8 }}>{formatCurrency(amount)}</span>
                </div>
              </div>
              <div style={{ height: 6, background: 'var(--surface-alt)', borderRadius: 99 }}>
                <div style={{ width: `${maxAging > 0 ? (amount / maxAging) * 100 : 0}%`, height: '100%', background: color, borderRadius: 99 }} />
              </div>
            </div>
          ))}
          <div style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--border)', marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-2)', fontWeight: 600 }}>Total Overdue</span>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#FF4757' }}>{formatCurrency(summary.total_overdue_amount)}</span>
            </div>
          </div>
        </div>

        {/* NPL trend */}
        <div className="card">
          <SectionHeader title="NPL Trend (6 months)" subtitle="Defaulted loan count over time" />
          <SparkBars data={npl_trend} valueKey="defaulted" labelKey="month" color="#FF4757" />
          <SparkLabels data={npl_trend} labelKey="month" />
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'var(--surface-alt)', borderRadius: 8 }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>Active Portfolio</span>
              <span style={{ fontSize: '0.8rem', fontWeight: 700 }}>{formatCurrency(summary.active_portfolio)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(255,71,87,0.06)', borderRadius: 8, marginTop: 6, border: '1px solid rgba(255,71,87,0.15)' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>NPL Exposure</span>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#FF4757' }}>{formatCurrency(summary.npl_value)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function Analytics() {
  const [tab, setTab] = useState(0);

  return (
    <div>
      <div className="page-header">
        <h1>Analytics</h1>
        <p>Real-time platform intelligence — users, lending portfolio, and risk exposure</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', background: 'var(--surface)', borderRadius: 12, padding: 4, border: '1px solid var(--border)', width: 'fit-content' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            padding: '0.5rem 1.1rem', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: tab === i ? 700 : 400,
            background: tab === i ? 'linear-gradient(135deg, rgba(0,200,150,0.2), rgba(0,200,150,0.08))' : 'transparent',
            color: tab === i ? 'var(--primary)' : 'var(--text-2)',
            transition: 'all .15s',
          }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 0 && <UserAnalytics />}
      {tab === 1 && <LoanAnalytics />}
      {tab === 2 && <NplAnalytics />}
    </div>
  );
}
